import { DownOutlined, UpOutlined } from '@ant-design/icons'
import Avatar from '@lmc/ui-kit/src/dataDisplay/Avatar'
import { Checkbox } from '@lmc/ui-kit/src/dataDisplay/Checkbox'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { Trash } from '@lmc/ui-kit/src/systemIcons'
import type { BodyMapPickerReport } from '@lmc/validations'
// Aliased: `Body` is also the name of this file's content wrapper below.
import BodyMapIcon from 'components/icons/Body'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import styled, { useTheme } from 'styled-components'

// Deliberately shorter than the repo-wide DD-MM-YYYY: these rows are narrow and
// carry a lot of text, and the designs use the two-digit year.
const DATE_FORMAT = 'DD-MM-YY, HH:mm'

export type BodyMapItemProps = {
  expanded?: boolean
  /** Inside a group: no border of its own, since the group provides one. */
  nested?: boolean
  onRemove?: (reportId: string) => void
  onToggleExpanded?: (reportId: string) => void
  onToggleSelected?: (reportId: string) => void
  report: BodyMapPickerReport
  selected?: boolean
  showCheckbox?: boolean
  showRemove?: boolean
  testId?: string
}

const Row = styled.div<{ $nested: boolean; $selected: boolean }>`
  align-items: flex-start;
  background: ${({ theme }) => theme.color.neutral.color11};
  ${({ $nested, $selected, theme }) =>
    $nested
      ? 'border: none;'
      : `border: 1px solid ${
          $selected ? theme.color.press.primary : theme.color.neutral.color8
        };`}
  border-radius: ${({ theme }) => theme.properties.borderRadius.small};
  display: flex;
  gap: 12px;
  padding: 8px 12px;
`

// Fixed to the 40px icon-tile height so the checkbox stays centred on the
// header even when the row expands to show details.
const CheckboxSlot = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  height: 40px;
`

const Body = styled.div`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

const Header = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  width: 100%;
`

// Tied to the checkbox via htmlFor, so the icon, name and date all toggle
// selection. Rendered as a plain div on rows without a checkbox.
const SelectTarget = styled.label<{ $clickable: boolean }>`
  align-items: center;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'inherit')};
  display: flex;
  flex: 1 0 0;
  gap: 12px;
  min-width: 0;
`

const IconTile = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.color.category.healthRecords};
  border-radius: ${({ theme }) => theme.properties.borderRadius.small};
  display: flex;
  flex-shrink: 0;
  height: 40px;
  justify-content: center;
  width: 40px;
`

const Titles = styled.div`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  min-width: 0;
`

// The design's 14/22 regular in neutral grey; `Text` has no matching level.
// Shared with BodyMapGroup so grouped and standalone dates stay in step.
export const DateLabel = styled.div`
  color: ${({ theme }) => theme.color.neutral.color4};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.font.weight.regular};
  line-height: 22px;
`

// Sized here rather than via `Text`, whose levels are 16/24, 14/16 and 12/16 —
// none of which is the design's 14/22 body-small.
const Details = styled.div`
  color: ${({ theme }) => theme.color.neutral.color2};
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: 4px;
  line-height: 22px;
`

// All chevrons in the picker are 16px; the antd default here is smaller.
const DescriptionToggle = styled(Button)`
  .anticon {
    font-size: 16px;
  }
`

const Author = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.color.neutral.color4};
  display: flex;
  font-size: 14px;
  gap: 8px;
  line-height: 22px;
`

const BodyMapItem = ({
  expanded = false,
  nested = false,
  onRemove,
  onToggleExpanded,
  onToggleSelected,
  report,
  selected = false,
  showCheckbox = true,
  showRemove = false,
  testId
}: BodyMapItemProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const theme = useTheme()

  // Nothing to reveal means no toggle. A body map can carry a comment without a
  // description, so the description alone does not decide this.
  const hasDetails = Boolean(
    report.lastComment || report.lastDescription || report.lastLoggedBy
  )

  // A body map nobody has logged against since is shown by when it was added.
  const dateLabel = report.lastActivityOn
    ? `${t(incidentReportsConstants.UPDATED_ON)} ${dayjs(report.lastActivityOn).format(DATE_FORMAT)}`
    : `${t(incidentReportsConstants.ADDED_ON)} ${dayjs(report.createdOn).format(DATE_FORMAT)}`

  const checkboxId = showCheckbox ? `body-map-select-${report.id}` : undefined

  return (
    <Row $nested={nested} $selected={selected} data-testid={testId}>
      {showCheckbox && (
        <CheckboxSlot>
          <Checkbox
            aria-label={report.title ?? undefined}
            checked={selected}
            data-testid={testId && `${testId}-checkbox`}
            id={checkboxId}
            onChange={() => onToggleSelected?.(report.id)}
          />
        </CheckboxSlot>
      )}
      <Body>
        <Header>
          <SelectTarget
            $clickable={showCheckbox}
            as={showCheckbox ? 'label' : 'div'}
            htmlFor={checkboxId}
          >
            <IconTile data-testid={testId && `${testId}-icon`}>
              <BodyMapIcon
                fill={theme.color.neutral.color11}
                size="20"
                viewBox="0 0 24 24"
              />
            </IconTile>
            <Titles>
              <Text level={1} weight="medium">
                {report.title}
              </Text>
              <DateLabel>{dateLabel}</DateLabel>
            </Titles>
          </SelectTarget>
          {showRemove && (
            <Button
              data-testid={testId && `${testId}-remove`}
              onClick={() => onRemove?.(report.id)}
              variant="textDanger"
            >
              <Trash color={theme.color.status.danger} />{' '}
              {t(incidentReportsConstants.REMOVE)}
            </Button>
          )}
          {hasDetails && (
            <DescriptionToggle
              data-testid={testId && `${testId}-toggle-description`}
              onClick={() => onToggleExpanded?.(report.id)}
              variant="text"
            >
              {expanded ? (
                <>
                  {t(incidentReportsConstants.HIDE_DESCRIPTION)} <UpOutlined />
                </>
              ) : (
                <>
                  {t(incidentReportsConstants.SHOW_DESCRIPTION)}{' '}
                  <DownOutlined />
                </>
              )}
            </DescriptionToggle>
          )}
        </Header>
        {expanded && (
          <>
            <Details>
              {report.lastDescription && <p>{report.lastDescription}</p>}
              {report.resolved && (
                <p>{t(incidentReportsConstants.INJURY_RESOLVED)}</p>
              )}
              {report.lastComment && <p>{report.lastComment}</p>}
            </Details>
            {report.lastLoggedBy && (
              <Author>
                <span>{`${t(incidentReportsConstants.CREATED_BY)}`}</span>
                <Avatar
                  size={24}
                  url={report.lastLoggedBy.picture ?? undefined}
                />
                <span>{report.lastLoggedBy.name}</span>
              </Author>
            )}
          </>
        )}
      </Body>
    </Row>
  )
}

export default BodyMapItem
